import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AlertBanner,
  Box,
  Loader,
  Switch,
  Typography,
} from '@saviynt/design-system';

import FormBuilder, {
  FieldTypes,
} from '../../../../components/FormBuilder/components/Form/FormBuilder';
import IntegrationPageError from '../../../../components/IntegrationPageError/IntegrationPageError';
import {
  getBaseUrlObNextGen,
  getObNextGenApiUrlLists,
} from '../../../../constants/obNextGenConstants';
import useLocalization from '../../../../hooks/use-localize';
import { type RootState } from '../../../../redux/obNextGenStore';
import Logger from '../../../../services/loggerService';
import {
  type Attribute,
  type DerivedAttributes,
  type FormInputFields,
} from '../../data-layer/ApplicationCard';
import {
  useFormTemplateMutation,
  useGetConnectionListQuery,
} from '../../data-layer/ApplicationSlice';
import { type DiscoverySaveConnectionState } from '../../data-layer/DiscoverySaveConnectionSlice';
import { resetDiscoverySaveConnectionState } from '../../data-layer/DiscoverySaveConnectionSlice';
import ExistingConnection from '../existing-connection/ExistingConnection';

import './ApplicationCardContent.css';

interface ApplicationContentProps {
  applicationType: string;
  connectionType: string;
  appTypeKey: number;
  image: string;
  createNewConnection: boolean;
  connectionJsonString: (jsonStr: string) => void;
  getAllFormInputValue: (status: FormInputFields[]) => void;
  disableContentFooter: (value: boolean) => void;
  showExistingConnectionFooter: (value: boolean) => void;
  existingPayload: (value: any) => void;
}

/**
 * Application Card Content
 */
function ApplicationCardContent({
  applicationType,
  connectionType,
  appTypeKey,
  image,
  createNewConnection,
  connectionJsonString,
  getAllFormInputValue,
  disableContentFooter,
  showExistingConnectionFooter,
  existingPayload,
}: ApplicationContentProps) {
  const dispatch = useDispatch();
  const formatMessage = useLocalization();
  const [guideMe, setGuideMe] = useState(false);
  const [inputFields, setInputField] = useState<FormInputFields[]>([]);
  const [showBanner, setShowBanner] = useState(false);
  const discoveryTestStatus = useSelector(
    (state: RootState): DiscoverySaveConnectionState =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      state.discoverySaveConnection
  );
  const { data: connectionListResponse, isLoading: loadingConnectionList } =
    useGetConnectionListQuery({ appTypeKey, page: 0, size: 25, search: '' });

  const [formTemplate, { isLoading, data: formTemplateData, isError }] =
    useFormTemplateMutation();
  const { GET_APPLICATION_ICONS } = getObNextGenApiUrlLists();
  const generatePayloadApi = useMemo(
    () => ({
      applicationType,
      connectionType,
      appTypeKey,
      parentCategoryName: 'configuration',
      categoryName: 'Connection',
    }),
    [appTypeKey, applicationType, connectionType]
  );

  useEffect(() => {
    if (
      !connectionListResponse ||
      connectionListResponse.list.length === 0 ||
      createNewConnection
    ) {
      // eslint-disable-next-line no-void
      void formTemplate(generatePayloadApi);
    }
  }, [
    generatePayloadApi,
    formTemplate,
    connectionListResponse,
    createNewConnection,
  ]);

  useEffect(() => {
    if (
      discoveryTestStatus.data !== null ||
      discoveryTestStatus.errorData !== null
    ) {
      setShowBanner(true);
    }
  }, [discoveryTestStatus]);

  const testConnectionAttribute: Attribute[] = [];
  const fields = !isLoading
    ? formTemplateData?.configuration.categories[0]?.subcategories[0]?.groups[0]?.attributes.filter(
        (attr: Attribute) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const derived: DerivedAttributes = JSON.parse(attr.derived);

          if (derived.test_connection) {
            testConnectionAttribute.push(attr);
          }

          return derived.show_onboarding === true;
        }
      )
    : [];

  const formTitle =
    formTemplateData?.configuration.categories[0]?.subcategories[0]?.titleText;

  const sortedFields = fields?.sort((a, b) => a.configOrder - b.configOrder);

  const isInputTypePassword = (value: string): boolean =>
    value === 'Password text field';

  const handleFieldType = (value: string): FieldTypes | null => {
    if (value === 'Password text field' || value === 'Textfield Singleline') {
      return FieldTypes.TextInputSingleLine;
    }

    return null;
  };

  const handleInputField = (status: {
    attributeName: string;
    value: string;
    hasError: boolean;
    payloadAtrributeName: string;
  }) => {
    setInputField((prevFields) => {
      const updated = [...prevFields];
      const index = updated.findIndex(
        (field) => field.attributeName === status.attributeName
      );

      if (index > -1) {
        updated[index] = status;
      } else {
        updated.push(status);
      }

      return updated;
    });
  };

  const convertBrToNewlines = (text: string): string =>
    text.replace(/<br\s*\/?>/gi, '\n');

  const formFields = sortedFields?.map((field) => ({
    type: handleFieldType(field.formatDesc),
    label: formatMessage(field.attributeDisplayName, 'Default Labels'),
    pattern: field.validationPattern,
    password: isInputTypePassword(field.formatDesc),
    inputDescription: convertBrToNewlines(
      formatMessage(field.attributeValueExample, 'Default Label Description') ||
        ''
    ),
    required: field.mandatory === 'yes',
    guideMe,
    payloadAtrributeName: field.answerAttribute,
    attributeName: field.attributeName,
    inputValueStatus: handleInputField,
    fieldDisabled: discoveryTestStatus.loading,
  }));

  const handleTestConnectionJson = (
    attr: Attribute[],
    formInputFields: FormInputFields[]
  ) => {
    let connectionJson = attr[0]?.attributeDefaultValue;
    const pattern = '@[a-zA-Z0-9_]+@';
    const regex = new RegExp(pattern);

    formInputFields.forEach((input) => {
      if (
        input.attributeName &&
        regex.test(input.attributeName) &&
        !input.hasError &&
        input.value !== ''
      ) {
        const tag = input.attributeName;

        connectionJson = connectionJson
          ? connectionJson.replace(tag, input.value)
          : '';
      }
    });

    connectionJsonString(connectionJson ?? '');
  };

  useEffect(() => {
    handleTestConnectionJson(testConnectionAttribute, inputFields);
    getAllFormInputValue(inputFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputFields]);

  const handleBannerClose = () => {
    setShowBanner(false);
    dispatch(resetDiscoverySaveConnectionState());
  };

  const handleReload = async () => {
    try {
      await formTemplate(generatePayloadApi);
    } catch (err) {
      Logger.error('Failed to reload:', err);
    }
  };

  if (isLoading || loadingConnectionList) {
    return (
      <Loader
        className='ApplicationCardContent-IntialLoader'
        color='brand'
        format='main'
        kind='loop'
      />
    );
  }

  if (
    connectionListResponse &&
    connectionListResponse.total > 0 &&
    !createNewConnection
  ) {
    const firstOptionKey = connectionListResponse.list[0].externalConnectionKey;

    showExistingConnectionFooter(true);

    return (
      firstOptionKey && (
        <ExistingConnection
          appTypeKey={appTypeKey}
          image={image}
          title={formTitle}
          total={connectionListResponse.total}
          applicationType={applicationType}
          connectionType={connectionType}
          firstOptionKey={firstOptionKey}
          existingConnectionPayload={existingPayload}
        />
      )
    );
  }

  showExistingConnectionFooter(false);

  if (isError) {
    disableContentFooter(true);

    return (
      <IntegrationPageError
        illustrationKind='Error'
        heading='INTEGRATIONS.DISCOVERY.ERROR.HEADING'
        subHeading='INTEGRATIONS.DISCOVERY.ERROR.SUBHEADING'
        reload={handleReload}
      />
    );
  }

  return (
    <Box className='ApplicationCardContent'>
      <Box className='ApplicationCardContent-Header'>
        <Box className='ApplicationCardContent-Header-Container'>
          <Box className='ApplicationCardContent-Header-Container-Title'>
            <img
              className='ApplicationCardContent-Header-Container-Title-Image'
              src={`${getBaseUrlObNextGen()}${GET_APPLICATION_ICONS}/${image}`}
              alt='Application icon'
            />
            <Typography kind='h3' desktop='h3-d' mobile='h3-d'>
              {formTitle ? (
                formatMessage(formTitle, 'Default Title')
              ) : (
                <Loader kind='dots' format='inline' color='brand' />
              )}
            </Typography>
          </Box>
          <div className='ApplicationCardContent-Header-Container-Help'>
            <Typography kind='body2'>
              {formatMessage(
                'INTEGRATION.DISCOVERY.SETUP.GUIDE_ME',
                'Guide Me'
              )}
            </Typography>
            <Switch isChecked={guideMe} onChange={() => setGuideMe(!guideMe)} />
          </div>
        </Box>
        <Box
          className={
            guideMe ? 'ApplicationCardContent-Header-Descprition' : ''
          }>
          {guideMe && (
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {formatMessage(
                'INTEGRATIONS.DISCOVERY.CONNECTION.APPLICATION.SETUP.DESCRIPTION',
                'Discovery Setup'
              )}
            </Typography>
          )}
        </Box>
      </Box>
      <Box className='ApplicationCardContent-Banner'>
        {discoveryTestStatus.data !== null && (
          <AlertBanner
            isVisible={showBanner}
            colorTheme='Success'
            title='Test Successful'
            description={
              discoveryTestStatus.data.connectionStatus === 'SUCCESS'
                ? discoveryTestStatus.data.msg ?? ''
                : ''
            }
            onCancel={() => {
              handleBannerClose();
            }}
          />
        )}
        {discoveryTestStatus.errorData !== null && (
          <AlertBanner
            isVisible={showBanner}
            colorTheme='Critical'
            title='Test Failed'
            description={JSON.stringify(discoveryTestStatus.errorData)}
            onCancel={() => {
              handleBannerClose();
            }}
          />
        )}
      </Box>
      <Box className='ApplicationCardContent-Content'>
        {isLoading ? (
          <div className='ApplicationCardContent-Content-Loader'>
            <Loader color='brand' format='main' kind='loop' />
          </div>
        ) : (
          <FormBuilder
            className='ApplicationCardContent-Content-Container'
            fields={formFields ?? []}
          />
        )}
      </Box>
    </Box>
  );
}

export default ApplicationCardContent;
